/**
 * Appearance Store
 *
 * Persists appearance settings (card size, aspect ratio, thumbnail scale)
 * independently for Artboard (locations) and Storyboard views.
 *
 * - Settings are per-user, NOT per-project.
 * - Saved to the backend over gRPC (debounced 500ms).
 * - Hydrated on login so settings survive tab closes / device switches.
 * - Artboard appearance is global across all location folders (not per-folder).
 */

import { create } from 'zustand';
import { saveVisionBoard } from '../services/visionAccountClient.js';
import { currentFirebaseUser } from '../services/firebaseClient.js';

const SIZE_PRESETS = { S: 140, M: 260, L: 380 };

function presetFromSize(val) {
  if (val <= 180) return 'S';
  if (val <= 320) return 'M';
  return 'L';
}

const defaultSettings = () => ({
  imageSize: 260,
  cardSizePreset: 'M',
  aspectRatio: 'horizontal',   // horizontal | square | vertical
  thumbnailScale: 'fill',      // fit | fill
});

// ── Debounced persistence ──
// One timer per slice so rapid changes batch into a single API call.
const persistTimers = { artboard: null, storyboard: null, visionboard: null };

function persistSlice(sliceName) {
  if (persistTimers[sliceName]) clearTimeout(persistTimers[sliceName]);
  const owner = currentFirebaseUser()?.uid;
  persistTimers[sliceName] = setTimeout(() => {
    // Another tab can switch the shared Firebase user before this tab hears of it; the request
    // would then carry the new user's token
    if (currentFirebaseUser()?.uid !== owner) return;
    const state = useAppearanceStore.getState();
    const data = state[sliceName];
    const persist = saveVisionBoard(sliceName, data);
    persist.catch((err) => {
      console.error(`Failed to persist ${sliceName} appearance:`, err);
    });
  }, 500);
}

export const useAppearanceStore = create((set, get) => ({
  // Separate state for each view
  artboard: defaultSettings(),
  storyboard: defaultSettings(),
  visionboard: defaultSettings(),

  /**
   * Hydrate from server response.
   * @param {Object} prefs - { appearance_artboard: {...}, appearance_storyboard: {...} }
   */
  hydrate: (prefs) => {
    const updates = {};
    if (prefs?.appearance_artboard) {
      const a = prefs.appearance_artboard;
      const preset = a.cardSizePreset ?? presetFromSize(a.imageSize ?? 260);
      // If a preset is set, use the current preset value (handles preset value changes)
      const size = preset && SIZE_PRESETS[preset] ? SIZE_PRESETS[preset] : (a.imageSize ?? 260);
      updates.artboard = {
        imageSize: size,
        cardSizePreset: preset,
        aspectRatio: a.aspectRatio ?? 'horizontal',
        thumbnailScale: a.thumbnailScale ?? 'fill',
      };
    }
    if (prefs?.appearance_storyboard) {
      const s = prefs.appearance_storyboard;
      const preset = s.cardSizePreset ?? presetFromSize(s.imageSize ?? 260);
      const size = preset && SIZE_PRESETS[preset] ? SIZE_PRESETS[preset] : (s.imageSize ?? 260);
      updates.storyboard = {
        imageSize: size,
        cardSizePreset: preset,
        aspectRatio: s.aspectRatio ?? 'horizontal',
        thumbnailScale: s.thumbnailScale ?? 'fill',
      };
    }
    if (prefs?.appearance_visionboard) {
      const v = prefs.appearance_visionboard;
      const preset = v.cardSizePreset ?? presetFromSize(v.imageSize ?? 260);
      const size = preset && SIZE_PRESETS[preset] ? SIZE_PRESETS[preset] : (v.imageSize ?? 260);
      updates.visionboard = {
        imageSize: size,
        cardSizePreset: preset,
        aspectRatio: v.aspectRatio ?? 'horizontal',
        thumbnailScale: v.thumbnailScale ?? 'fill',
      };
    }
    if (Object.keys(updates).length > 0) {
      set(updates);
    }
  },

  reset: () => {
    for (const sliceName of Object.keys(persistTimers)) {
      clearTimeout(persistTimers[sliceName]);
      persistTimers[sliceName] = null;
    }
    set({ artboard: defaultSettings(), storyboard: defaultSettings(), visionboard: defaultSettings() });
  },

  // ── Artboard setters ──

  setArtboardSize: (val) => {
    set((state) => ({
      artboard: {
        ...state.artboard,
        imageSize: val,
        cardSizePreset: presetFromSize(val),
      },
    }));
    persistSlice('artboard');
  },

  setArtboardSizePreset: (preset) => {
    set((state) => ({
      artboard: {
        ...state.artboard,
        imageSize: SIZE_PRESETS[preset],
        cardSizePreset: preset,
      },
    }));
    persistSlice('artboard');
  },

  setArtboardAspectRatio: (value) => {
    set((state) => ({
      artboard: { ...state.artboard, aspectRatio: value },
    }));
    persistSlice('artboard');
  },

  setArtboardThumbnailScale: (value) => {
    set((state) => ({
      artboard: { ...state.artboard, thumbnailScale: value },
    }));
    persistSlice('artboard');
  },

  // ── Storyboard setters ──

  setStoryboardSize: (val) => {
    set((state) => ({
      storyboard: {
        ...state.storyboard,
        imageSize: val,
        cardSizePreset: presetFromSize(val),
      },
    }));
    persistSlice('storyboard');
  },

  setStoryboardSizePreset: (preset) => {
    set((state) => ({
      storyboard: {
        ...state.storyboard,
        imageSize: SIZE_PRESETS[preset],
        cardSizePreset: preset,
      },
    }));
    persistSlice('storyboard');
  },

  setStoryboardAspectRatio: (value) => {
    set((state) => ({
      storyboard: { ...state.storyboard, aspectRatio: value },
    }));
    persistSlice('storyboard');
  },

  setStoryboardThumbnailScale: (value) => {
    set((state) => ({
      storyboard: { ...state.storyboard, thumbnailScale: value },
    }));
    persistSlice('storyboard');
  },

  // ── Visionboard setters ──

  setVisionboardSize: (val) => {
    set((state) => ({
      visionboard: { ...state.visionboard, imageSize: val, cardSizePreset: presetFromSize(val) },
    }));
    persistSlice('visionboard');
  },
  setVisionboardSizePreset: (preset) => {
    set((state) => ({
      visionboard: { ...state.visionboard, imageSize: SIZE_PRESETS[preset], cardSizePreset: preset },
    }));
    persistSlice('visionboard');
  },
  setVisionboardAspectRatio: (value) => {
    set((state) => ({
      visionboard: { ...state.visionboard, aspectRatio: value },
    }));
    persistSlice('visionboard');
  },
  setVisionboardThumbnailScale: (value) => {
    set((state) => ({
      visionboard: { ...state.visionboard, thumbnailScale: value },
    }));
    persistSlice('visionboard');
  },
}));
